/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { PrimaryButton } from '@fluentui/react/lib/Button';
import { ILabelStyleProps, ILabelStyles, Label } from '@fluentui/react/lib/Label';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { getTheme } from '@fluentui/style-utilities';
import { classNamesFunction } from '@fluentui/utilities';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { CollapsedErrorMessage } from 'src/core/components/CollapsedErrorMessage';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { isDevelopmentEnvironment, isStagingEnvironment } from 'src/core/utils/api/environment';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { AzureOpenAiDeployment } from 'src/customText/verticals/autotagTriggerModal/types';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';

type AOAIResourceConnectorProps = {
    dropdownWidth?: string;
    selectedAzureOpenAiDeploymentId: string;
    selectedAzureOpenAiResource: AzureCsAccountStore;
    onAzureOpenAiDeploymentIdChange: (newDeploymentId: string) => void;
    onAzureOpenAiResourceChange: (newAccount: AzureCsAccountStore) => void;
};

const messages = defineMessages({
    connect: { id: 'gptPrediction.aOAIResourceConnector.connect', defaultMessage: 'Connect' },
    connecting: { id: 'gptPrediction.aOAIResourceConnector.connecting', defaultMessage: 'Connecting' },
    haveConnectedOpenAiResource: {
        id: 'gptPrediction.aOAIResourceConnector.haveConnectedOpenAiResource',
        defaultMessage: 'You have a connected Azure Open AI resource'
    },
    createAzureOpenAi: {
        id: 'gptPrediction.aOAIResourceConnector.createAzureOpenAi',
        defaultMessage: 'Create a new Azure OpenAI resource'
    },

    selectAzureOpenAiResourceLabel: {
        id: 'gptPrediction.aOAIResourceConnector.selectAzureOpenAiResourceLabel',
        defaultMessage: 'Select your Azure Open AI resource'
    },
    invalidPermissions: {
        id: 'gptPrediction.aOAIResourceConnector.invalidPermissions',
        defaultMessage: 'Your Language resource must have "Cognitive Services User" role in the Azure open AI resource.'
    },
    selectAzureOpenAiDeploymentLabel: {
        id: 'gptPrediction.aOAIResourceConnector.selectAzureOpenAiDeploymentLabel',
        defaultMessage: 'Select your Azure Open AI deployment'
    },
    selectAzureOpenAiDeploymentInfo: {
        id: 'gptPrediction.aOAIResourceConnector.selectAzureOpenAiDeploymentInfo',
        defaultMessage: 'To use this feature here, you will need to deploy a model in AOAI and select it from here'
    },
    permissionsNote: {
        id: 'gptPrediction.aOAIResourceConnector.permissionsNote',
        defaultMessage: 'Your Language resource will be assigned the role "Cognitive Services User" to your Azure Open AI resource'
    },
    deploymentNameNote: {
        id: 'gptPrediction.aOAIResourceConnector.deploymentNameNote',
        defaultMessage: 'We recommend selecting a deployment with the model ‘text-davinci-002’ for the best results'
    }
});

const labelStyles = classNamesFunction<ILabelStyleProps, ILabelStyles>()({
    root: { display: 'flex', fontWeight: 400, selectors: { '::after': { paddingRight: 0 } } }
});

export const AOAIResourceConnector = observer((props: AOAIResourceConnectorProps) => {
    const {
        dropdownWidth = '100%',
        selectedAzureOpenAiResource,
        onAzureOpenAiResourceChange,
        onAzureOpenAiDeploymentIdChange,
        selectedAzureOpenAiDeploymentId
    } = props;
    const { localize } = useLocalization();
    const connectingTracker = useProgressTracker();
    const { dispatcher, rootStore } = useGlobalState();
    const azureOpenAiResourcePermissionsTracker = useProgressTracker({ persistOnComplete: true });
    const resourcesStoreHouse = useLocalStore<ResourceStoreHouse<AzureCsAccountStore>>(createResourceStoreHouse);
    const azureOpenAiDeployments = useLocalStore<ResourceStoreHouse<AzureOpenAiDeployment>>(createResourceStoreHouse);

    const currentResource = rootStore.azureStore.session.csAccountStore;
    const [selectedAzureOpenAiResourceId, setSelectedAzureOpenAiResourceId] = React.useState(currentResource.ownedAzureOpenAiResourceId);

    const azureOpenAiResource = resourcesStoreHouse.items.find(r => r.path === selectedAzureOpenAiResourceId);

    React.useEffect(() => {
        if (selectedAzureOpenAiResourceId !== selectedAzureOpenAiResource?.id && resourcesStoreHouse.tracker.isCompleted) {
            onAzureOpenAiResourceChange(azureOpenAiResource);
        }
    }, [selectedAzureOpenAiResourceId, resourcesStoreHouse.tracker.isCompleted]);

    React.useEffect(() => {
        dispatcher.dispatch('getAzureCsAccountsBySubscriptionId', {
            localStoreHouse: resourcesStoreHouse,
            filterPredicate: r => r.kind === 'OpenAI',
            id:
                isDevelopmentEnvironment() || isStagingEnvironment()
                    ? 'e54a2925-af7f-4b05-9ba1-2155c5fe8a8e'
                    : rootStore.azureStore.session.subscriptionId
        });
    }, []);

    React.useEffect(() => {
        if (Boolean(azureOpenAiResource?.path) && currentResource.ownedAzureOpenAiResourceId === azureOpenAiResource?.path) {
            dispatcher.dispatch('checkAndSetResourceAccountRequiredRoles', {
                resourceToCheck: azureOpenAiResource,
                principalId: currentResource.principalId,
                tracker: azureOpenAiResourcePermissionsTracker,
                roles: LuisConstants.Azure.CognitiveServicesUserRoleAssignmentPermissions,
                roleDefinition: LuisConstants.Azure.AzureRoleDefinitionId.CognitiveServicesUser
            });
        }
    }, [azureOpenAiResource?.id, currentResource.ownedAzureOpenAiResourceId]);

    React.useEffect(() => {
        if (
            resourcesStoreHouse.tracker.isCompleted &&
            currentResource.ownedAzureOpenAiResourceId &&
            azureOpenAiResourcePermissionsTracker.isCompleted
        ) {
            dispatcher.dispatch('getAzureOpenAiDeployments', {
                toastData: {},
                resource: azureOpenAiResource,
                deploymentsStoreHouse: azureOpenAiDeployments
            });
        }
    }, [
        resourcesStoreHouse.tracker.isCompleted,
        currentResource.ownedAzureOpenAiResourceId,
        azureOpenAiResourcePermissionsTracker.isCompleted
    ]);

    const labelOnRender = (label: string, tooltipText: string | React.ReactNode, isRequired: boolean = true) => (
        <Stack horizontal verticalAlign="center">
            <Label styles={labelStyles} required={isRequired}>
                {label}
            </Label>
            <CalloutButton content={tooltipText} />
        </Stack>
    );

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <Stack tokens={{ childrenGap: 3 }}>
                <Stack horizontal tokens={{ childrenGap: 13 }} verticalAlign="end">
                    <LuisDropdown
                        aria-required
                        styles={{ root: { width: dropdownWidth } }}
                        selectedKey={selectedAzureOpenAiResourceId}
                        isLoading={resourcesStoreHouse.tracker.isRunning}
                        ariaLabel={localize(messages.selectAzureOpenAiResourceLabel)}
                        placeholder={localize(messages.selectAzureOpenAiResourceLabel)}
                        onChange={(_, o) => setSelectedAzureOpenAiResourceId(o.key as string)}
                        disabled={!currentResource.isIdentityEnabled || connectingTracker.isRunning}
                        onRenderLabel={() =>
                            labelOnRender(
                                localize(messages.selectAzureOpenAiResourceLabel),
                                localize(messages.selectAzureOpenAiResourceLabel)
                            )
                        }
                        options={resourcesStoreHouse.items.map(r => ({
                            key: r.path,
                            text: r.name,
                            data: { secondaryText: `(${r.region}, ${r.sku.code})` }
                        }))}
                    />
                    {selectedAzureOpenAiResourceId !== currentResource.ownedAzureOpenAiResourceId && (
                        <PrimaryButton
                            disabled={connectingTracker.isRunning}
                            onClick={() =>
                                dispatcher.dispatch('updateAzureCsAccountOpenAiResourceByPath', {
                                    tracker: connectingTracker,
                                    ownedAzureOpenAiResourceId: selectedAzureOpenAiResourceId
                                })
                            }>
                            <Stack horizontal tokens={{ childrenGap: 5 }}>
                                {connectingTracker.isRunning && <Spinner size={SpinnerSize.small} />}
                                <LuisText>{localize(connectingTracker.isRunning ? messages.connecting : messages.connect)}</LuisText>
                            </Stack>
                        </PrimaryButton>
                    )}
                </Stack>
                {currentResource.ownedAzureOpenAiResourceId ? (
                    <LuisText>{localize(messages.haveConnectedOpenAiResource)}</LuisText>
                ) : (
                    <ExternalLink href="https://aka.ms/create-aoai">{localize(messages.createAzureOpenAi)}</ExternalLink>
                )}
                {selectedAzureOpenAiResourceId !== currentResource.ownedAzureOpenAiResourceId && (
                    <LuisText>{localize(messages.permissionsNote)}</LuisText>
                )}
                {connectingTracker.isFailed && <CollapsedErrorMessage error={connectingTracker.error} />}
                {azureOpenAiResourcePermissionsTracker.isFailed && (
                    <CollapsedErrorMessage error={new Error(localize(messages.invalidPermissions))} />
                )}
            </Stack>

            <Stack>
                <LuisDropdown
                    aria-required
                    styles={{ root: { width: dropdownWidth } }}
                    selectedKey={selectedAzureOpenAiDeploymentId}
                    isLoading={azureOpenAiDeployments.tracker.isRunning}
                    ariaLabel={localize(messages.selectAzureOpenAiDeploymentLabel)}
                    placeholder={localize(messages.selectAzureOpenAiDeploymentLabel)}
                    onChange={(_, o) => onAzureOpenAiDeploymentIdChange(o.key as string)}
                    options={azureOpenAiDeployments.items.map(d => ({ key: d.id, text: d.deploymentName }))}
                    onRenderLabel={() =>
                        labelOnRender(
                            localize(messages.selectAzureOpenAiDeploymentLabel),
                            localize(messages.selectAzureOpenAiDeploymentInfo)
                        )
                    }
                    disabled={
                        connectingTracker.isFailed ||
                        connectingTracker.isRunning ||
                        !selectedAzureOpenAiResourceId ||
                        resourcesStoreHouse.tracker.isRunning ||
                        azureOpenAiResourcePermissionsTracker.isFailed
                    }
                />
                <LuisText variant="smallPlus" color={getTheme().palette.neutralSecondary}>
                    {localize(messages.deploymentNameNote)}
                </LuisText>
            </Stack>
        </Stack>
    );
});
